import { Fragment } from "react";

import i18next from "i18next";
import moment from "moment";
import { useSelector } from "react-redux";

import { webTransferTimeFormat } from "../../../config";

import {
  checkIsNightFlightAndIsChangeAirport,
  getCityString,
  getDayByFormat,
  getFreeSeatsLabel,
  getServiceClass,
  getTransferTime,
} from "../../../utils";

import { useToggle } from "../../../hooks";

import {
  AirlineAndSitsBlock,
  ArrivalBlock,
  DepartureBlock,
  Divider,
  DurationBlock,
  FlightDetails,
  FlightInfo,
  FlightNumber,
  FlightNumbersBlock,
  FlightSegment,
  FlightTimingsBlock,
  Header,
  StatusBadge,
  TransferBlock,
  TransferTime,
  Wrapper,
} from "./style";

const RouteItemComponent = ({ route, onOpenRulesModal }) => {
  const { included, validatingSupplier } = useSelector(state => state.prebook);
  const { isTrue: isShowedDetails, toggle } = useToggle();

  const { language, t } = i18next;

  const departureSegment = route.segments[0];
  const { is_refund, is_change, is_charter, baggage, hand_luggage, hand_luggage_weight, baggage_weight } = departureSegment;
  const arrivalSegment = route.segments[route.segments.length - 1];

  const renderDepartureInfo = () => {
    const { time, airport, terminal } = departureSegment.departure;

    return (
      <DepartureBlock>
        <span className="airport">{airport}</span>
        <span className="terminal">
          {t("terminal")}: {terminal}
        </span>
        <span className="time">{time.split(" ")[1]}</span>
        <span className="date">{getDayByFormat(time, "dddd DD MMMM", language)}</span>
      </DepartureBlock>
    );
  };

  const renderArrivalInfo = () => {
    const { time, airport, terminal } = arrivalSegment.arrival;

    return (
      <ArrivalBlock>
        <span className="airport">{airport}</span>
        <span className="terminal">
          {t("terminal")}: {terminal}
        </span>
        <span className="time">{time.split(" ")[1]}</span>
        <span className="date">{getDayByFormat(time, "dddd DD MMMM", language)}</span>
      </ArrivalBlock>
    );
  };

  const renderDurationTime = duration => {
    const hours = Math.trunc(duration / 3600);
    const minutes = Math.floor((duration - hours * 3600) / 60);

    return `${hours} ${t("hour_short")} ${minutes} ${t("minutes_short")}`;
  };

  const calculatePercents = () => {
    const parts = [];

    route.segments.forEach((segment, index) => {
      if (index === 0) {
        parts.push({
          percent: ((segment.duration * 100) / route.duration).toFixed(2),
          type: "segment",
          info: `${segment.operation_supplier}-${segment.carrier_number}, ${renderDurationTime(segment.duration)}`,
        });
      } else {
        const prevDate = route.segments[index - 1]?.arrival.time;
        const actualDate = segment.departure.time;
        const prevDateUnix = moment(prevDate, webTransferTimeFormat).unix();
        const actualDateUnix = moment(actualDate, webTransferTimeFormat).unix();
        const unixDifference = actualDateUnix - prevDateUnix;

        parts.push({
          percent: ((unixDifference * 100) / route.duration).toFixed(2),
          type: "transfer",
          info: `${t("transfer")}: ${getCityString(
            included,
            language,
            segment.departure.city,
            segment.departure.airport
          )}, ${segment.departure.airport}, (${getTransferTime(route, index, "ч", "м")})`,
        });
        parts.push({
          percent: ((segment.duration * 100) / route.duration).toFixed(2),
          type: "segment",
          info: `${segment.operation_supplier}-${segment.carrier_number}, ${renderDurationTime(segment.duration)}`,
        });
      }
    });

    return (
      <div className="flight-line">
        {parts.map((part, index) => (
          <div
            style={{
              height: part.type === "transfer" ? 1 : 3,
              width: `${part.percent}%`,
            }}
            key={index}
            title={part.info}
          >
            <span></span>
          </div>
        ))}
      </div>
    );
  };

  console.log(route);
  return (
    <Wrapper>
      <Header isOrangeBorder={route.index === 1 || route.index === 3}>
        <FlightNumbersBlock>
          {route.segments.map((segment, index) => (
            <FlightNumber key={index}>
              {t("flight")} {segment.operation_supplier}-{segment.carrier_number}
            </FlightNumber>
          ))}
        </FlightNumbersBlock>
        <AirlineAndSitsBlock>
          <StatusBadge>{route.segments.length > 1 ? t("with_transfer") : t("direct_flight")}</StatusBadge>
          <StatusBadge className="warning">
            {getFreeSeatsLabel(departureSegment.free_seats, t("free_seats_1"), t("free_seats_2_4"), t("free_seats_5"))}
          </StatusBadge>
          {is_charter && <StatusBadge className="charter">{t("charter")}</StatusBadge>}
        </AirlineAndSitsBlock>
      </Header>

      {/* <AirlineLogo src={`/suppliers/${validatingSupplier}.png`} alt={`supplier-${validatingSupplier}-logo`} /> */}

      <FlightTimingsBlock>
        {renderDepartureInfo()}

        <DurationBlock>
          {calculatePercents()}
          <div className="duration-time">{renderDurationTime(route.duration)}</div>
          <div className="segments">
            {route.segments
              .slice(0, -1)
              .map(segment => segment.arrival.airport)
              .join(", ")}
          </div>
        </DurationBlock>

        {renderArrivalInfo()}
      </FlightTimingsBlock>

      <FlightDetails>
        <Divider />
        {route.segments.map((segment, index) => {
          let aircraftName = segment.aircraft;

          if (included.aircraft[segment.aircraft]) {
            aircraftName = included.aircraft[segment.aircraft].name.en;
          } else {
            aircraftName = "";
          }

          const isTransfer = index > 0;
          let transferTime = "";

          if (isTransfer) {
            transferTime = getTransferTime(route, index, t("hour_short"), t("minutes_short"));
          }

          const [isChangeAirport, isNightTransfer] = checkIsNightFlightAndIsChangeAirport(route, index);

          return (
            <Fragment key={index}>
              <FlightSegment>
                <FlightInfo>
                  <h2 className="title">{t("class")}</h2>
                  <div className="value uppercase">{getServiceClass(segment.service_class, "values")}</div>
                </FlightInfo>

                <FlightInfo>
                  <h2 className="title">{t("baggage")}</h2>
                  {baggage_weight && baggage_weight !== 0 ? (
                    <span>
                      {baggage && baggage > 1 ? `${baggage} × ` : ""}
                      {baggage_weight} {t("kg")}
                    </span>
                  ) : baggage && baggage !== "0PC" ? (
                    <span className="value">{typeof baggage === "number" ? `${baggage} PC` : baggage}</span>
                  ) : (
                    <span className="value">{t("baggage_not_included")}</span>
                  )}
                  {hand_luggage_weight && hand_luggage_weight !== 0 ? (
                    <>
                      {t("hand_luggage")}{" "}
                      <strong>
                        {hand_luggage && hand_luggage > 1 ? `${hand_luggage} × ` : ""}
                        {hand_luggage_weight} {t("kg")}
                      </strong>
                    </>
                  ) : hand_luggage && hand_luggage !== "0PC" ? (
                    <span className="subValue">
                      {t("hand_luggage")} <strong>{hand_luggage} pc</strong>
                    </span>
                  ) : (
                    <span className="subValue">{t("hand_luggage_not_included")}</span>
                  )}
                  {((baggage && baggage !== "0PC" && (!baggage_weight || baggage_weight === 0)) ||
                    (hand_luggage &&
                      hand_luggage !== "0PC" &&
                      (!hand_luggage_weight || hand_luggage_weight === 0))) && (
                    <span className="subValue">
                      {t("clarify_weight_contact_operator")}
                    </span>
                  )}
                </FlightInfo>

                <FlightInfo>
                  <h2 className="title">{t("exchange")}</h2>
                  <span className="value">{is_change ? t("allowed") : t("non_exchangeable")}</span>
                  <span className="subValue">{is_change ? t("contact_operator") : ""}</span>
                </FlightInfo>

                <FlightInfo>
                  <h2 className="title">{t("refund")}</h2>
                  <span className="value">{is_refund ? t("allowed") : t("non_refundable")}</span>
                  <span className="subValue">{is_refund ? t("contact_operator") : ""}</span>
                </FlightInfo>
              </FlightSegment>

              {isTransfer && (
                <TransferBlock>
                  <TransferTime>
                    {getCityString(included, language, segment.departure.city, segment.departure.airport)}:{" "}
                    {t("transfer")} {transferTime}
                    {isChangeAirport && `, ${t("change_airport")}`}
                    {isNightTransfer && `, ${t("night_flight")}`}
                  </TransferTime>
                </TransferBlock>
              )}
            </Fragment>
          );
        })}
      </FlightDetails>
    </Wrapper>
  );
};

export default RouteItemComponent;
